import { normalizeUnitCode } from '../validation/normalizeText.js';
import { roundQuantity } from '../validation/quantity.js';
/** Precision for derived factors stored on `ProductUnit` (spec 0050 / request 0283). */
export const DERIVED_FACTOR_DECIMALS = 6;
const ATTR_LABELS = {
    grammage: 'gramatura',
    entryWidth: 'largura de entrada',
    unitWeight: 'peso unitário',
};
/**
 * Map a free unit code onto the closed derivation table.
 * Case-insensitive; `M²` / `M2` both become `M2` via {@link normalizeUnitCode}.
 */
export function normalizeRecognizedMeasureCode(code) {
    const n = normalizeUnitCode(code);
    if (n === 'KG' || n === 'M' || n === 'M2' || n === 'UN')
        return n;
    return null;
}
function positiveAttr(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}
function missing(field) {
    return {
        ok: false,
        reason: 'missing-attr',
        missingField: field,
        missingLabel: ATTR_LABELS[field],
    };
}
function ok(raw, formulaKey, explanation) {
    return {
        ok: true,
        factor: roundQuantity(raw, DERIVED_FACTOR_DECIMALS),
        formulaKey,
        explanation,
    };
}
/**
 * Pure derivation of base units per 1 line unit from product physical attributes.
 *
 * Table (base × line → factor), with g = grammage (g/m²), w = entryWidth (m),
 * p = unitWeight (kg). Width is always entryWidth (D8). Outside the closed code
 * set, returns `not-recognized` — caller keeps a typed factor.
 */
export function deriveProductUnitFactor(input) {
    const base = normalizeRecognizedMeasureCode(input.baseUnit);
    const line = normalizeRecognizedMeasureCode(input.lineUnitCode);
    if (base == null || line == null) {
        return { ok: false, reason: 'not-recognized' };
    }
    if (base === line) {
        return { ok: false, reason: 'no-formula' };
    }
    const g = positiveAttr(input.grammage);
    const w = positiveAttr(input.entryWidth);
    const p = positiveAttr(input.unitWeight);
    if (base === 'KG' && line === 'M2') {
        if (g == null)
            return missing('grammage');
        return ok(g / 1000, 'kg-m2', `Gramatura ${g} gramas por metro quadrado, dividido por 1000`);
    }
    if (base === 'KG' && line === 'M') {
        if (g == null)
            return missing('grammage');
        if (w == null)
            return missing('entryWidth');
        return ok((w * g) / 1000, 'kg-m', `Largura de entrada ${w} metros, vezes gramatura ${g}, dividido por 1000`);
    }
    if (base === 'M' && line === 'M2') {
        if (w == null)
            return missing('entryWidth');
        return ok(1 / w, 'm-m2', `Um dividido pela largura de entrada ${w} metros`);
    }
    if (base === 'M' && line === 'KG') {
        if (g == null)
            return missing('grammage');
        if (w == null)
            return missing('entryWidth');
        return ok(1000 / (g * w), 'm-kg', `Mil dividido pela gramatura ${g} vezes a largura de entrada ${w}`);
    }
    if (base === 'M2' && line === 'M') {
        if (w == null)
            return missing('entryWidth');
        return ok(w, 'm2-m', `Largura de entrada ${w} metros`);
    }
    if (base === 'M2' && line === 'KG') {
        if (g == null)
            return missing('grammage');
        return ok(1000 / g, 'm2-kg', `Mil dividido pela gramatura ${g}`);
    }
    if (base === 'UN' && line === 'KG') {
        if (p == null)
            return missing('unitWeight');
        return ok(1 / p, 'un-kg', `Um dividido pelo peso unitário ${p} quilogramas`);
    }
    return { ok: false, reason: 'no-formula' };
}
/** True when base × line is in the closed derivation table (attrs may still be missing). */
export function canDeriveProductUnitFactor(baseUnit, lineUnitCode) {
    const result = deriveProductUnitFactor({
        baseUnit,
        lineUnitCode,
        grammage: 1,
        entryWidth: 1,
        unitWeight: 1,
    });
    return result.ok === true || result.reason === 'missing-attr';
}
export function deriveProductUnitFactorFromProduct(product, lineUnitCode) {
    return deriveProductUnitFactor({
        baseUnit: product.unit,
        lineUnitCode,
        grammage: product.grammage,
        entryWidth: product.entryWidth,
        unitWeight: product.unitWeight,
    });
}
//# sourceMappingURL=deriveProductUnitFactor.js.map